import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react'
import { CompanyContext } from './CompanyContext'
import { ScheduledPostContext } from './ScheduledPostContext'
import dayjs from 'dayjs'
import { ruleToDateObj } from 'utils/rulesUtils'
import { Notification, toast } from 'components/ui'
import {
    useRuleGroupList,
    useUpdateRuleGroup,
    useDeleteRuleGroup,
} from 'api/hooks/useRuleGroups'

/**
 * Thin compatibility shim that preserves the existing `RuleGroupsContext`
 * consumer interface while delegating fetch + mutate to React Query hooks.
 * The auto-scheduling computation (combining rule groups with scheduled posts)
 * is preserved verbatim.
 */

const RuleGroupsContext = React.createContext({
    ruleGroups: [],
    isLoading: false,
    autoscheduledPosts: [],
    isLoadingAutoscheduledPosts: false,
    getRuleGroups: () => {},
    updateRuleGroupFields: () => {},
    deleteRuleGroup: () => {},
    isAutomatedPostGroup: () => false,
    getAutomatedPublishAt: () => undefined,
})

const RuleGroupsProvider = ({ children }) => {
    const { getScheduledPostGroups, triggerUpdatePostGroups } = useContext(
        ScheduledPostContext
    )
    const { company } = useContext(CompanyContext)

    const {
        data: ruleGroupsResp,
        isLoading,
        refetch,
    } = useRuleGroupList(
        {
            include: ['tags', 'rules'],
            sort: '-id',
            filter: { company_id: company?.id },
        },
        {
            enabled: !!company?.id,
            select: (resp) => resp?.data ?? resp ?? [],
        }
    )
    const ruleGroups = ruleGroupsResp || []

    const [autoscheduledPosts, setAutoscheduledPosts] = useState([])
    const [isLoadingAutoscheduledPosts, setIsLoadingAutoscheduledPosts] =
        useState(false)

    const { mutateAsync: updateRuleGroupMutate } = useUpdateRuleGroup()
    const { mutateAsync: deleteRuleGroupMutate } = useDeleteRuleGroup()

    const getRuleGroups = useCallback(() => {
        refetch()
    }, [refetch])

    const updateRuleGroupFields = useCallback(
        async (id, fields = undefined) => {
            try {
                await updateRuleGroupMutate({ id, ...fields })
            } catch (err) {
                toast.push(
                    <Notification
                        title={err?.message || String(err)}
                        type="warning"
                    />
                )
            }
        },
        [updateRuleGroupMutate]
    )

    const deleteRuleGroup = useCallback(
        async (id) => {
            try {
                await deleteRuleGroupMutate(id)
            } catch (err) {
                toast.push(
                    <Notification
                        title={err?.message || String(err)}
                        type="warning"
                    />
                )
            }
        },
        [deleteRuleGroupMutate]
    )

    const isAutomatedPostGroup = useCallback(
        (postGroup) => {
            let flag = false
            if (postGroup?.publish_at) return false
            if (ruleGroups?.length) {
                const activeRuleGroups = ruleGroups.filter((e) => !!e.active)
                activeRuleGroups.forEach((ruleGroup) => {
                    if (!postGroup?.tags || !postGroup?.tags?.length) {
                        if (!ruleGroup?.tags || !ruleGroup?.tags?.length) {
                            flag = true
                        }
                    }
                    postGroup?.tags?.forEach((tag) => {
                        if (ruleGroup?.tags?.find((t) => t?.id == tag?.id)) {
                            flag = true
                        }
                    })
                })
            }
            return flag
        },
        [ruleGroups]
    )

    const getAutomatedPublishAt = useCallback(
        (postGroup) =>
            autoscheduledPosts?.find((e) => e?.id == postGroup?.id)?.publish_at,
        [autoscheduledPosts]
    )

    // Auto-scheduling computation (unchanged from legacy implementation).
    const getAutoscheduledPosts = useCallback(async () => {
        if (isLoadingAutoscheduledPosts) return
        setIsLoadingAutoscheduledPosts(true)
        let _autoscheduledPostGroups = []
        try {
            if (ruleGroups?.length) {
                const activeRuleGroups = ruleGroups.filter((e) => !!e.active)
                if (activeRuleGroups.length) {
                    const results = await Promise.allSettled(
                        activeRuleGroups.map((ruleGroup) =>
                            getScheduledPostGroups(
                                {
                                    rule_group_id: ruleGroup?.id,
                                    publish_at_null: 1,
                                    status: 'pending',
                                },
                                'id',
                                true
                            )
                        )
                    )
                    const activeRuleGroupsWithPostGroups = results.map(
                        (r, i) => ({
                            ...activeRuleGroups[i],
                            postGroups: r?.value || [],
                        })
                    )
                    let dates = activeRuleGroupsWithPostGroups.map(
                        (ruleGroup) => {
                            const postGroups = ruleGroup?.postGroups
                            let _dates = []
                            if (postGroups?.length) {
                                const rules = ruleGroup?.rules
                                if (rules?.length) {
                                    _dates = rules.map((rule) => {
                                        const date = dayjs(ruleToDateObj(rule))
                                        return Array.apply(
                                            null,
                                            Array(54)
                                        ).map((_, i) =>
                                            date.add(i, 'weeks').toDate()
                                        )
                                    })
                                    _dates = _dates.flat()
                                    const today = new Date()
                                    _dates = _dates
                                        .filter((e) => e > today)
                                        .sort((a, b) => a - b)
                                        .map((date) => ({ date, ruleGroup }))
                                }
                            }
                            return _dates
                        }
                    )
                    dates = dates
                        .flat()
                        .sort((a, b) => a?.date - b?.date)
                    dates.forEach((dateObj) => {
                        const ruleGroup = dateObj?.ruleGroup
                        const postGroups = ruleGroup?.postGroups?.filter(
                            (e) =>
                                !_autoscheduledPostGroups?.find(
                                    (i) => e?.id == i?.id
                                )
                        )
                        const postGroup = postGroups?.shift()
                        if (postGroup) {
                            _autoscheduledPostGroups.push({
                                ...postGroup,
                                publish_at: dayjs(dateObj?.date)
                                    .add(
                                        !ruleGroup?.random_minutes
                                            ? 0
                                            : dayjs(postGroup?.created_at)
                                                  .unix() %
                                                  parseFloat(
                                                      ruleGroup?.random_minutes
                                                  ),
                                        'minutes'
                                    )
                                    .toDate()
                                    ?.toISOString(),
                                ruleGroup,
                            })
                        }
                    })
                }
            }
        } catch (_) {}
        setAutoscheduledPosts(_autoscheduledPostGroups.flat())
        setIsLoadingAutoscheduledPosts(false)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ruleGroups, getScheduledPostGroups])

    useEffect(() => {
        getAutoscheduledPosts()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(ruleGroups), triggerUpdatePostGroups])

    const value = useMemo(
        () => ({
            ruleGroups,
            isLoading,
            autoscheduledPosts,
            isLoadingAutoscheduledPosts,
            getRuleGroups,
            updateRuleGroupFields,
            deleteRuleGroup,
            isAutomatedPostGroup,
            getAutomatedPublishAt,
        }),
        [
            ruleGroups,
            isLoading,
            autoscheduledPosts,
            isLoadingAutoscheduledPosts,
            getRuleGroups,
            updateRuleGroupFields,
            deleteRuleGroup,
            isAutomatedPostGroup,
            getAutomatedPublishAt,
        ]
    )

    return (
        <RuleGroupsContext.Provider value={value}>
            {children}
        </RuleGroupsContext.Provider>
    )
}

export { RuleGroupsContext, RuleGroupsProvider }
